import React, { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFilterDropdown } from './useFilterDropdown';
import { useFilterSearch } from './useFilterSearch';
import { useFilterSelection } from './useFilterSelection';
import { Icon, mainTheme } from '@plecto/ui';
import { Loader } from '../loaders/Loader';
import styled, { css, keyframes } from 'styled-components';
import { StyledFilterCustomItem } from './StyledFilterItem';
import { FilterItem } from './types';
import { FilterStatus } from './FilterStatus';
import { FilterDropdownMenuHeader } from './FilterDropdownMenuHeader';
import { FilterDropdownMenuFooter } from './FilterDropdownMenuFooter';

// Mirrors the @plecto/ui dropdown: same size scale, same tokens, same motion.
// ponytail: copied rather than imported — the package exports only `Dropdown`,
// and this filter menu is a multi-select with a header, footer and tree rows.
// Import the primitives instead once @plecto/ui exports them.
const BOX_HEIGHT = 36;
const PRESSED_INSET = 0.5;
const MENU_PADDING = 4;
const MENU_ITEM_GAP = 2;
const MENU_MAX_HEIGHT = 310;
const MENU_MOTION_MS = parseInt(mainTheme.motion.duration.default, 10);

const transition = (...props: string[]) => props
	.map(prop => `${prop} ${mainTheme.motion.duration.default} ${mainTheme.motion.easing.default}`)
	.join(', ');

// A `from`-only keyframe animates out of the collapsed frame and into whatever
// the element's own rules say; a `to`-only one does the reverse.
const expand = keyframes`
  from {
    grid-template-rows: 0fr;
    opacity: 0;
  }
`;

const collapse = keyframes`
  to {
    grid-template-rows: 0fr;
    opacity: 0;
  }
`;

const StyledChevron = styled(Icon)`
  flex: 0 1 auto;
  color: ${mainTheme.form.dropdown.box.chevron};
  transition: ${transition('color', 'transform')};
`;

const StyledDropdown = styled.div<{
	$open: boolean,
	$selected: boolean
}>`
  font-family: ${mainTheme.font};
  font-weight: 510;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
  cursor: pointer;
  height: ${BOX_HEIGHT}px;
  gap: 4px;
  padding: 0 8px 0 10px;
  font-size: 14px;
  border-radius: 4px;
  border: 1px solid ${mainTheme.form.dropdown.box.border};
  // @plecto/ui leaves a closed box transparent because it always sits on a
  // themed panel. A filter bar does not, so it paints the base surface itself.
  background: ${mainTheme.surface[1]};
  color: ${mainTheme.form.dropdown.box.color};
  transition: ${transition('background', 'border-color', 'color')};

  &:hover {
    border-color: ${mainTheme.form.dropdown.box.hover.border};
    color: ${mainTheme.form.dropdown.box.hover.color};

    ${StyledChevron} {
      color: ${mainTheme.form.dropdown.box.hover.chevron};
    }
  }

  &:active {
    border-color: ${mainTheme.form.dropdown.box.pressed.border};
    box-shadow: inset 0 0 0 ${PRESSED_INSET}px ${mainTheme.form.dropdown.box.pressed.border};

    ${StyledChevron} {
      color: ${mainTheme.form.dropdown.box.pressed.chevron};
    }
  }

  // A filter with a selection rests brighter than an untouched one — that is the
  // only cue that the list behind it is narrowed.
  ${({ $selected }) => $selected && css`
    border-color: ${mainTheme.border.active};
    color: ${mainTheme.form.dropdown.box.hover.color};

    ${StyledChevron} {
      color: ${mainTheme.form.dropdown.box.hover.chevron};
    }
  `}

  // Open is a resting state of its own, so it restates hover and press rather
  // than layering on top of the closed ones.
  ${({ $open }) => $open && css`
    background: ${mainTheme.form.dropdown.box.open.background};
    border-color: ${mainTheme.form.dropdown.box.open.border};
    color: ${mainTheme.form.dropdown.box.open.color};
    box-shadow: inset 0 0 0 ${PRESSED_INSET}px ${mainTheme.form.dropdown.box.open.border};

    ${StyledChevron} {
      color: ${mainTheme.form.dropdown.box.open.chevron};
      transform: rotate(180deg);
    }

    &:hover,
    &:active {
      border-color: ${mainTheme.form.dropdown.box.open.engagedBorder};
      color: ${mainTheme.form.dropdown.box.open.engagedColor};
      box-shadow: none;

      ${StyledChevron} {
        color: ${mainTheme.form.dropdown.box.open.engagedChevron};
      }
    }
  `}
`;

const StyledLoaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

// The row track carries the whole open and close motion: 0fr collapses it to
// nothing, 1fr lets the content size it. Filling forwards holds the collapsed
// frame for the moment between the animation ending and the unmount.
const StyledMenuRoot = styled.div<{
	$fitContent: boolean;
	$maxWidth?: number;
	$closing?: boolean;
}>`
  position: absolute;
  width: ${props =>
		props.$fitContent ? 'auto' : (props.$maxWidth ? `${props.$maxWidth}px` : '100%')};
  left: 0;
  top: calc(100% + 5px);
  display: grid;
  grid-template-rows: 1fr;
  border-radius: 4px;
  box-shadow: ${mainTheme.form.dropdown.menu.shadow};
  z-index: 45;

  animation: ${props => props.$closing ? collapse : expand}
    ${MENU_MOTION_MS}ms
    ${mainTheme.motion.easing.default} forwards;

  // A closing menu is still on screen, and clicking a row that is on its way
  // out selects something the user is watching disappear.
  ${({ $closing }) => $closing && css`
    pointer-events: none;
  `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const StyledMenu = styled.div`
  font-family: ${mainTheme.font};
  box-sizing: border-box;
  // Inset highlight instead of a stroke — the menu lifts off the panel behind
  // it rather than being drawn onto it.
  box-shadow: ${mainTheme.shadow.inset};
  background: ${mainTheme.form.dropdown.menu.default};
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  padding: ${MENU_PADDING}px;
  max-height: ${MENU_MAX_HEIGHT}px;
  font-size: 14px;
  color: ${mainTheme.form.dropdown.menu.color};
  // Lets the grid row above collapse past the content height.
  min-height: 0;
  overflow: hidden;
`;

const StyledDropdownContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${MENU_ITEM_GAP}px;
  // Positioning context for the hover indicator: offsetTop of a row is measured
  // against this box, so the indicator scrolls with the rows.
  position: relative;
  min-height: 0;
  overflow: auto;
`;

// One rectangle for the whole menu, moved to whichever row the pointer is on,
// instead of a fill per row lighting up in place.
const HoverIndicator = styled.div<{ $visible?: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  border-radius: 2px;
  pointer-events: none;
  background: ${mainTheme.form.dropdown.menu.hover.background};
  box-shadow: ${mainTheme.shadow.inset};
  opacity: ${({ $visible }) => $visible ? 1 : 0};
  transition: ${['transform', 'height', 'opacity']
		.map(prop => `${prop} ${mainTheme.motion.duration.instant} ${mainTheme.motion.easing.default}`)
		.join(', ')};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

// The menu is positioned absolutely, so the root owns the positioning context.
// Without it the menu anchors to whatever ancestor happens to be positioned —
// on a filter bar inside a relative table header, that put it at the header's
// left edge instead of under the trigger.
const StyledRoot = styled.div`
  position: relative;
`;

const StyledNoElementsItem = styled(StyledFilterCustomItem)`
  color: ${mainTheme.form.dropdown.menu.disabled.color};
  cursor: default;
`;

type ChildrenProps = {
	items: FilterItem[];
	selection: string[];
	filteredItems: FilterItem[];
	onToggle: (id: string) => void;
	searchMode: boolean;
}

type FilterDropdownBaseProps = {
	className?: string;
	label: string;
	isSuccess?: boolean;
	isFetching?: boolean;
	items: FilterItem[];
	selection?: string[];
	onSelectionChange?: (selection: string[]) => unknown;
	onClose?: (selection: string[]) => void;
	onOpen?: () => void;
	children?: (props: ChildrenProps) => ReactNode | undefined;
	fitContent?: boolean;
	maxWidth?: number;
	compactLabel?: boolean;
	canSelectAll?: boolean;
	newLayout?: boolean;
	/** The leading filter funnel. Off for a bar that labels itself. */
	showIcon?: boolean;
}

export function FilterDropdownBase({
	canSelectAll = true,
	label = '',
	items = [],
	isFetching = false,
	selection: initiallySelected = [],
	onClose,
	onOpen,
	onSelectionChange,
	fitContent = false,
	maxWidth,
	compactLabel,
	children,
	className,
	newLayout = false,
	showIcon = true,
}: FilterDropdownBaseProps) {
	const { t } = useTranslation();
	const {
		searchInputValue,
		filteredItems,
		onSearchChange,
		forceChange: onSearchForceChange,
	} = useFilterSearch(items);

	const selectableItems = filteredItems.filter(item => !item.disabled);
	const selectableCount = selectableItems.length;
	const isEmpty = filteredItems.length === 0;

	const {
		onToggle: onToggleFilter,
		onToggleMany,
		allSelected,
		onDeselectAll,
		selectionChanged,
		selection,
	} = useFilterSelection({
		selectableItems,
		onSelectionChange,
		initiallySelected,
	});

	const {
		isOpen,
		onDropdownClose,
		onDropdownToggle,
		buttonRef,
		menuRef,
	} = useFilterDropdown({ onClose, onOpen, selection });

	const searchMode = Boolean(searchInputValue);
	const selectionActive = selection && selection.length > 0;

	// The menu has to outlive isOpen so its collapse can play. A timer rather
	// than animationend: the event never arrives when the animation is
	// interrupted or the tab is backgrounded, and a menu that never unmounts is
	// worse than one that unmounts a frame late.
	const [menuMounted, setMenuMounted] = useState(false);

	useEffect(() => {
		if (isOpen) {
			setMenuMounted(true);
			return;
		}
		const collapsed = setTimeout(() => setMenuMounted(false), MENU_MOTION_MS);
		return () => clearTimeout(collapsed);
	}, [isOpen]);

	// Where the sliding hover rectangle currently is. null parks it hidden.
	const [indicator, setIndicator] = useState<{ top: number, height: number } | null>(null);
	const moveIndicatorTo = (row: HTMLElement | null) =>
		setIndicator(row ? { top: row.offsetTop, height: row.offsetHeight } : null);

	const onCancel = () => {
		onDeselectAll();
		onDropdownClose([]);
	};

	const onSave = () => onDropdownClose(selection);

	return (
		<StyledRoot className={className}>
			<StyledDropdown
				onClick={onDropdownToggle}
				ref={buttonRef}
				$selected={selectionActive}
				$open={isOpen}>
				<FilterStatus
					active={selectionActive}
					selection={selection}
					label={label}
					compactLabel={compactLabel}
					items={items}
					newLayout={newLayout}
					showIcon={showIcon}
				/>
				<StyledChevron icon="dropdown-chevron" />
			</StyledDropdown>
			{menuMounted && <StyledMenuRoot
				$fitContent={fitContent}
				$maxWidth={maxWidth}
				$closing={!isOpen}
				ref={menuRef}>
				<StyledMenu>
					<FilterDropdownMenuHeader
						onSearchForceChange={onSearchForceChange}
						onSearchChange={onSearchChange}
						searchInputValue={searchInputValue}
						selectableCount={selectableCount}
						onToggle={onToggleMany}
						allSelected={allSelected}
						canSelectAll={canSelectAll}
					/>
					<StyledDropdownContainer
						onMouseOver={e => moveIndicatorTo(
							(e.target as HTMLElement).closest<HTMLElement>('[data-filter-row]:not([data-disabled])')
						)}
						onMouseLeave={() => moveIndicatorTo(null)}>
						<HoverIndicator
							aria-hidden
							$visible={!!indicator}
							style={{
								transform: `translateY(${indicator?.top ?? 0}px)`,
								height: indicator?.height ?? 0,
							}} />
						{isFetching ? <StyledLoaderWrapper><Loader size={'sm'} /></StyledLoaderWrapper> :
							isEmpty && <StyledNoElementsItem>
								{searchInputValue ?
									t('No results found for "{{searchPhrase}}".', { searchPhrase: searchInputValue }) :
									t('Nothing to display yet.')}
							</StyledNoElementsItem>}
						{children?.({
							items,
							selection,
							filteredItems: selectableItems,
							onToggle: onToggleFilter,
							searchMode,
						})}
					</StyledDropdownContainer>
					{!newLayout && (
						<FilterDropdownMenuFooter
							onCancel={onCancel}
							disabled={!selectionChanged}
							onSave={onSave}
						/>
					)}
				</StyledMenu>
			</StyledMenuRoot>}
		</StyledRoot>
	);
}
